// working to consolidate these into a single domain object with dynamic storage
import { colors } from '@mui/material';
import type { DataItem, DataItemId, MappingItem } from '@pn/core/domain/data';
import type { User } from '@pn/core/domain/user';
import {
  createWorkspaceItem,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { setMapping } from '@pn/core/storage/workspace/mappings';
import { pnWorkspaceItems } from '@pn/core/storage/workspace/pnWorkspaceItems';
import { generateTemporaryId } from '@pn/core/utils/id';
import { intelMappings } from '@pn/resources/intel-mappings';
import { muiColorPalette } from '@pn/services/color/colorPalettes';
import { addAlpha } from '@pn/services/utils/color';
import { isEmpty, isNil, startCase } from 'lodash-es';
import { buildQuery, type Query } from '../query';

/**
 * @deprecated Use `DataItem` instead
 */
export type IntelDataItem = {
  id: string;
  activityItemIds: string[];
  date: string; // ISO date string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type IntelQueryType =
  | 'well_licenses'
  | 'confidential_wells'
  | 'declassified_wells'
  | 'land_postings'
  | 'land_results'
  | 'aer_transfers'
  | 'mineral_rights_transfers'
  | 'well_license_transfers'
  | 'wells_by_production'
  | 'wells_by_liquid_production'
  | 'wells_by_gas_production'
  | 'well_licence_mismatches'
  | 'liquid_ip3'
  | 'liquid_ip12'
  | 'gas_ip3'
  | 'gas_ip12'
  | 'expired_minerals'
  | 'zone_changes'
  | 'wellbore_recompletes'
  | 'crown_mineral_activity';

const DefaultNames: Record<IntelQueryType, string> = {
  well_licenses: 'Well Licences',
  aer_transfers: 'AER Transfers',
  mineral_rights_transfers: 'Mineral Rights Transfers',
  well_license_transfers: 'Well Licence Transfers',
  wells_by_production: 'Top Wells',
  wells_by_liquid_production: 'Top Liquid Wells',
  wells_by_gas_production: 'Top Gas Wells',
  well_licence_mismatches: 'Well Mismatches',
  liquid_ip3: 'Top Liquid IP3 Wells',
  liquid_ip12: 'Top Liquid IP12 Wells',
  gas_ip3: 'Top Gas IP3 Wells',
  gas_ip12: 'Top Gas IP12 Wells',
  land_postings: 'Land Postings',
  land_results: 'Land Results',
  confidential_wells: 'Confidential Wells',
  declassified_wells: 'Declassified Wells',
  expired_minerals: 'Crown Reversions',
  zone_changes: 'Zone Changes',
  wellbore_recompletes: 'Recompleted Wells',
  crown_mineral_activity: 'Crown Mineral Activity',
};

export function generateDefaultItemName(
  dataType: IntelQueryType | string
): string {
  const name = DefaultNames[dataType as IntelQueryType];
  return name ? name : startCase(dataType);
}

export type IntelWorkspaceItem = WorkspaceItem & {
  disabled: boolean;
  mapPlottingEnabled: boolean;
  isFetching: boolean;
  failedToFetch?: boolean;
  linkedReportId?: string;
  hideFromWorkspace?: boolean;
  editingDisabled: boolean;
  _needsProcessing?: boolean;
  _needsVisualProcessing?: boolean;
  _avoidLoadingUntilVisible?: boolean; // optimization flag
  templateId?: string;
};

const gridsWorkspaceItem = pnWorkspaceItems['grids'];
const mineralRightsWorkspaceItem = pnWorkspaceItems['mineral_rights'];
const wellsWorkspaceItem = pnWorkspaceItems['wells'];
const crownMineralActivityItem = pnWorkspaceItems['crown_mineral_activity'];

function mapIntelWorkspaceItemToWorkspaceItemSource(dataType: string): {
  sourceItem: WorkspaceItem;
  dataType: string;
} {
  switch (dataType) {
    case 'mineral_rights_transfers':
    case 'zone_changes':
      return {
        sourceItem: mineralRightsWorkspaceItem,
        dataType: 'mineral_rights',
      };
    case 'aer_transfers':
    case 'land_postings':
    case 'land_results':
    case 'expired_minerals':
      return { sourceItem: gridsWorkspaceItem, dataType: 'grids' };
    case 'crown_mineral_activity':
      return {
        sourceItem: crownMineralActivityItem,
        dataType: 'crown_mineral_activity',
      };
    default:
      return { sourceItem: wellsWorkspaceItem, dataType: 'wells' };
  }
}

const userToUse = {
  id: '1',
  email: '',
  name: { firstName: '', lastName: '' },
  company: '',
  title: '',
} as User;

export function createIntelWorkspaceItem(
  params: IntelWorkspaceItemParams
): IntelWorkspaceItem {
  const {
    id = generateTemporaryId(),
    query,
    color,
    name,
    linkedReportId,
    _avoidLoadingUntilVisible,
    plotOnMapWhenAdded = false,
    templateId,
  } = params;
  // TODO: create or hook up to an actual source item instead of the default PN ones
  const { sourceItem: temporaryWorkspaceSourceItem } =
    mapIntelWorkspaceItemToWorkspaceItemSource(query.dataType);
  const mapping = intelMappings[query.dataType];
  // TODO: all intel data items use this, but dont want in actual mapping quite yet
  const activityItemMapping: MappingItem = {
    domainType: 'object',
    field: 'activityItemIds',
    source: 'activityItemIds',
    ui: {
      label: 'Activity Item IDs',
      isNotSortable: true,
      isNotRenderable: true,
    },
  };
  const updatedMapping = {
    ...mapping,
    activityItemIds: activityItemMapping,
  };

  query.fields = Object.entries(updatedMapping)
    .filter(([_key, value]) => {
      return !isNil(value) && value.ui?.isNotRenderable !== true;
    })
    .map(([key]) => key)
    .filter((key) => !params.hiddenFields?.includes(key));

  const workspaceItem = createWorkspaceItem(
    {
      source: 'item',
      id,
      sourceItem: { ...temporaryWorkspaceSourceItem },
      isTemporary: true,
      origin: 'boe_intel',
      queryOptions: query,
      extraStyle: {
        color: color ? color : getDefaultColor(query.dataType),
      },
    },
    userToUse
  );

  workspaceItem.name = name ?? generateDefaultItemName(query.dataType);
  workspaceItem.dataType = query.dataType;
  setMapping(workspaceItem.dataType, updatedMapping);
  return {
    ...workspaceItem,
    disabled: false,
    editingDisabled: false,
    mapPlottingEnabled: plotOnMapWhenAdded,
    isFetching: false,
    _needsProcessing: true,
    _needsVisualProcessing: plotOnMapWhenAdded,
    _avoidLoadingUntilVisible,
    linkedReportId,
    hideFromWorkspace: !isNil(linkedReportId),
    templateId: templateId ?? query.dataType,
  };
}

export function buildIntelQueryObject(
  dataType: IntelQueryType,
  params: Partial<Query>
): Query {
  const mapping = intelMappings[dataType];
  const fields = mapping ? Object.keys(mapping) : [];
  return buildQuery({ id: generateTemporaryId(), ...params, dataType, fields });
}

type IntelWorkspaceItemParams = {
  id?: string;
  query: Query;
  name?: string;
  _avoidLoadingUntilVisible?: boolean;
  linkedReportId?: string;
  editingDisabled?: boolean;
  plotOnMapWhenAdded?: boolean;
  hiddenFields?: string[];
  color?: string;
  templateId?: string;
};

// TODO: once the gap between these closes, this will no longer be needed
export function convertToWorkspaceItem(
  intelWorkspaceItem: IntelWorkspaceItem
): WorkspaceItem {
  const checkedIds = getCheckedIntelDataItemIds(intelWorkspaceItem);
  const scopeIds: DataItemId[] = isEmpty(checkedIds)
    ? intelWorkspaceItem.tableDataItems.flatMap(
        (item: DataItem) => item.activityItemIds ?? []
      )
    : checkedIds;
  const { dataType, sourceItem } = mapIntelWorkspaceItemToWorkspaceItemSource(
    intelWorkspaceItem.query.dataType
  );

  const workspaceItem = createWorkspaceItem(
    {
      source: 'item',
      sourceItem,
      id: intelWorkspaceItem.id,
      isTemporary: true,
      origin: 'boe_intel',
      queryOptions: {
        dataType,
        scopeIds,
      },
      extraStyle: {
        color: getDefaultColor(intelWorkspaceItem.query.dataType),
      },
    },
    userToUse
  );
  workspaceItem.map = intelWorkspaceItem.map; // preserves map settings
  return workspaceItem;
}

function getDefaultColor(dataType: string) {
  switch (dataType) {
    case 'well_licenses':
      return muiColorPalette.getStaggeredColorByIndex(1);
    case 'aer_transfers':
      return addAlpha(muiColorPalette.getStaggeredColorByIndex(2), 0.8);
    case 'mineral_rights_transfers':
    case 'expired_minerals':
      return addAlpha(muiColorPalette.getStaggeredColorByIndex(4), 0.8);
    case 'well_license_transfers':
      return muiColorPalette.getStaggeredColorByIndex(3);
    case 'land_postings':
      return addAlpha(muiColorPalette.getStaggeredColorByIndex(6), 0.8);
    case 'land_results':
      return addAlpha(muiColorPalette.getStaggeredColorByIndex(0), 0.8);
    case 'wells_by_liquid_production':
      return colors.blue[500];
    case 'wells_by_gas_production':
      return colors.red[400];
    default:
      return muiColorPalette.getStaggeredColorByIndex(1);
  }
}

export function getIntelWorkspaceItemsForProcessing(
  intelWorkspaceItems: IntelWorkspaceItem[]
): IntelWorkspaceItem[] {
  return intelWorkspaceItems.filter(
    (item) =>
      item._needsProcessing &&
      !item.isFetching &&
      !item.disabled &&
      !item._avoidLoadingUntilVisible
  );
}

export function getWorkspaceItemsToAddOrUpdate(
  intelWorkspaceItems: IntelWorkspaceItem[]
): IntelWorkspaceItem[] {
  return intelWorkspaceItems.filter(
    (item) =>
      item.mapPlottingEnabled &&
      item._needsVisualProcessing &&
      !item.isFetching &&
      !item.disabled &&
      !isEmpty(item.tableDataItems)
  );
}

export function getIntelWorkspaceItemsToRemove(
  intelWorkspaceItems: IntelWorkspaceItem[]
): IntelWorkspaceItem[] {
  return intelWorkspaceItems.filter((item) => {
    const shouldRemove =
      !item.mapPlottingEnabled &&
      item._needsVisualProcessing &&
      !item.isFetching &&
      !item.disabled;
    return shouldRemove || isEmpty(item.tableDataItems);
  });
}

export function getCheckedIntelDataItemIds(
  intelWorkspaceItem: IntelWorkspaceItem
): DataItemId[] {
  const checkedItems = intelWorkspaceItem.tableDataItems.filter((item) =>
    intelWorkspaceItem.query.checkedIds.includes(item._id)
  );
  return checkedItems.flatMap((item) => item.activityItemIds ?? []);
}

export function getCheckedIntelData(
  intelWorkspaceItem: IntelWorkspaceItem
): DataItem[] {
  const checkedIds = intelWorkspaceItem.query.checkedIds;
  const mappedCheckedIds = checkedIds.map((id) => id.toString());
  const tableDataItems = intelWorkspaceItem.tableDataItems;
  return tableDataItems.filter((item) =>
    mappedCheckedIds.includes(item._id.toString())
  );
}
